/**
 * Signup Modal Component
 *
 * Displays a modal for new user registration.
 * Includes password strength validation and link to login.
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import MaterialIcon from '../MaterialIcon';
import './AuthModals.css';

const SignupModal = ({ isOpen, onClose, onSwitchToLogin }) => {
  const { signup } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError(''); // Clear error on input change
  };

  const validatePassword = (password) => {
    const errors = [];

    if (password.length < 8) {
      errors.push('at least 8 characters');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('one lowercase letter');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('one uppercase letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('one number');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate passwords match
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      // Validate password strength
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        setError(`Password must contain ${passwordErrors.join(', ')}`);
        setLoading(false);
        return;
      }

      // Attempt signup
      const result = await signup(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName
      );

      if (result.success) {
        setSuccess(true);
        // Show success message for 2 seconds, then close
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(result.error || 'Signup failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
    });
    setError('');
    setSuccess(false);
    setShowPassword(false);
    onClose();
  };

  if (!isOpen) return null;

  // Success state
  if (success) {
    return (
      <div className="auth-modal-overlay" onClick={handleClose}>
        <div className="auth-modal auth-modal-success" onClick={(e) => e.stopPropagation()}>
          <div className="auth-success-content">
            <MaterialIcon name="check_circle" className="auth-success-icon" />
            <h2>Welcome to ClothedEye!</h2>
            <p>Your account has been created successfully.</p>
            <p className="auth-success-subtext">
              We've sent a verification email to <strong>{formData.email}</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-modal-overlay" onClick={handleClose}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="auth-modal-header">
          <h2>Create Your Account</h2>
          <button className="auth-modal-close" onClick={handleClose} aria-label="Close modal">
            <MaterialIcon name="close" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-modal-form">
          {error && (
            <div className="auth-error-message">
              <MaterialIcon name="error" />
              <span>{error}</span>
            </div>
          )}

          {/* Name Fields */}
          <div className="auth-form-row">
            <div className="auth-form-group auth-form-group-half">
              <label htmlFor="signup-firstname">First Name</label>
              <input
                id="signup-firstname"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="First name"
                autoComplete="given-name"
                disabled={loading}
              />
            </div>

            <div className="auth-form-group auth-form-group-half">
              <label htmlFor="signup-lastname">Last Name</label>
              <input
                id="signup-lastname"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Last name"
                autoComplete="family-name"
                disabled={loading}
              />
            </div>
          </div>

          {/* Email Field */}
          <div className="auth-form-group">
            <label htmlFor="signup-email">Email *</label>
            <div className="auth-input-wrapper">
              <MaterialIcon name="email" />
              <input
                id="signup-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                required
                autoComplete="email"
                disabled={loading}
              />
            </div>
          </div>

          {/* Password Field */}
          <div className="auth-form-group">
            <label htmlFor="signup-password">Password *</label>
            <div className="auth-input-wrapper">
              <MaterialIcon name="lock" />
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password"
                required
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="auth-toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                <MaterialIcon name={showPassword ? 'visibility_off' : 'visibility'} />
              </button>
            </div>
            <div className="auth-password-requirements">
              <small>Must contain: 8+ characters, uppercase, lowercase, number</small>
            </div>
          </div>

          {/* Confirm Password Field */}
          <div className="auth-form-group">
            <label htmlFor="signup-confirm-password">Confirm Password *</label>
            <div className="auth-input-wrapper">
              <MaterialIcon name="lock" />
              <input
                id="signup-confirm-password"
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
                disabled={loading}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="auth-submit-button"
            disabled={loading || !formData.email || !formData.password || !formData.confirmPassword}
          >
            {loading ? (
              <>
                <span className="auth-spinner" />
                Creating account...
              </>
            ) : (
              <>
                <MaterialIcon name="person_add" />
                Create Account
              </>
            )}
          </button>

          {/* Terms */}
          <div className="auth-terms">
            <small>
              By creating an account, you agree to our Terms of Service and Privacy Policy.
            </small>
          </div>
        </form>

        {/* Footer - Switch to Login */}
        <div className="auth-modal-footer">
          <p>
            Already have an account?{' '}
            <button className="auth-link-button" onClick={onSwitchToLogin} disabled={loading}>
              Log in
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

SignupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSwitchToLogin: PropTypes.func.isRequired,
};

export default SignupModal;
